import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, CalendarRange, Filter, Wrench, AlertTriangle, X, Loader2, ArrowLeftRight, Bike, Plus, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const DAY = 86400000;
const STATO_COLORS = {
  richiesta: "bg-[#FF9500] text-black",
  in_attesa_caparra: "bg-[#f98125]/25 text-[#f98125] border-2 border-dashed border-[#f98125]",
  confermata: "bg-[#007AFF] text-white",
  in_corso: "bg-[#f98125] text-white",
  concluso: "bg-[#39FF14] text-black",
  annullata: "bg-zinc-600 text-zinc-200 opacity-50 line-through",
};
const STATO_LABEL = { richiesta: "Richiesta", in_attesa_caparra: "Attesa caparra", confermata: "Confermata", in_corso: "In corso", concluso: "Conclusa", annullata: "Annullata" };
const MOTIVO_LABEL = { manutenzione: "Manutenzione", fuori_servizio: "Fuori servizio", altro: "Altro" };

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const fmtDay = (d) => d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
const fmtWeekday = (d) => d.toLocaleDateString("it-IT", { weekday: "short" });
const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const isoDate = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;

export default function Calendar() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasPerm("rentals.edit");

  const [view, setView] = useState("week"); // day | week | month
  const [anchor, setAnchor] = useState(() => dayStart(new Date()));
  const [categoria, setCategoria] = useState("all");
  const [categories, setCategories] = useState([]);
  const [sede, setSede] = useState("all");
  const [sedi, setSedi] = useState([]);
  const [allBikes, setAllBikes] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [data, setData] = useState({ bikes: [], rentals: [], damages: [], indisponibilita: [] });
  const [loading, setLoading] = useState(true);

  // availability filter
  const [availOn, setAvailOn] = useState(false);
  const [availStart, setAvailStart] = useState(isoDate(new Date()));
  const [availEnd, setAvailEnd] = useState(isoDate(new Date(Date.now() + 2 * DAY)));

  const dayWidth = view === "week" ? 132 : 46;

  const { rangeStart, rangeEnd, days } = useMemo(() => {
    let start, n;
    if (view === "day") {
      start = dayStart(anchor); n = 1;
    } else if (view === "week") {
      const d = new Date(anchor); const wd = (d.getDay() + 6) % 7; // Monday start
      start = dayStart(new Date(d.getTime() - wd * DAY)); n = 7;
    } else {
      start = dayStart(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
      n = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    }
    const arr = Array.from({ length: n }, (_, i) => new Date(start.getTime() + i * DAY));
    return { rangeStart: start, rangeEnd: new Date(start.getTime() + n * DAY), days: arr };
  }, [anchor, view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start: rangeStart.toISOString(), end: rangeEnd.toISOString() };
      if (categoria !== "all") params.categoria_id = categoria;
      if (sede !== "all") params.sede_id = sede;
      const r = await api.get("/calendar", { params });
      setData(r.data);
    } catch (e) { toast.error("Errore caricamento calendario"); }
    finally { setLoading(false); }
  }, [rangeStart, rangeEnd, categoria, sede]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/bikes").then((r) => setAllBikes(r.data)).catch(() => {});
    api.get("/sedi").then((r) => setSedi(r.data)).catch(() => {});
    api.get("/timeslots", { params: { solo_attive: true } }).then((r) => setTimeslots(r.data)).catch(() => {});
  }, []);

  const nav = (dir) => {
    if (view === "day") setAnchor((a) => dayStart(new Date(a.getTime() + dir * DAY)));
    else if (view === "week") setAnchor((a) => dayStart(new Date(a.getTime() + dir * 7 * DAY)));
    else setAnchor((a) => dayStart(new Date(a.getFullYear(), a.getMonth() + dir, 1)));
  };

  // index helpers for positioning
  const colOffset = (date) => (dayStart(date).getTime() - rangeStart.getTime()) / DAY;
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  // bikes involved in a rental (multi-bici aware, con fallback single-bike)
  const rentalBikes = (r) => (r.bici && r.bici.length ? r.bici : [{ bike_id: r.bike_id, bike_numero: r.bike_numero }]);
  const isMultiRental = (r) => (r.bici?.length || 1) > 1;

  // availability filter: a bike is free across [availStart, availEnd) if no overlap with booking/damage/indispo
  const availSet = useMemo(() => {
    if (!availOn) return null;
    const s = dayStart(new Date(availStart)).getTime();
    const e = dayStart(new Date(availEnd)).getTime() + DAY; // inclusive end day
    const busy = new Set();
    data.rentals.forEach((r) => {
      if (["annullata", "concluso"].includes(r.stato)) return;
      const bi = new Date(r.inizio).getTime(), bf = new Date(r.fine).getTime();
      if (bi < e && s < bf) rentalBikes(r).forEach((rb) => busy.add(rb.bike_id));
    });
    data.damages.forEach((d) => busy.add(d.bike_id));
    data.indisponibilita.forEach((d) => {
      const di = new Date(d.data_inizio).getTime();
      const df = d.data_fine ? new Date(d.data_fine).getTime() : Infinity;
      if (di < e && s < df) busy.add(d.bike_id);
    });
    return new Set(data.bikes.filter((b) => !busy.has(b.id)).map((b) => b.id));
  }, [availOn, availStart, availEnd, data]);

  const visibleBikes = useMemo(
    () => (availSet ? data.bikes.filter((b) => availSet.has(b.id)) : data.bikes),
    [data.bikes, availSet]
  );

  // group blocks by bike
  const blocksByBike = useMemo(() => {
    const map = {};
    const ensure = (id) => (map[id] = map[id] || []);
    data.rentals.forEach((r) => {
      const sF = clamp((new Date(r.inizio).getTime() - rangeStart.getTime()) / DAY, 0, days.length);
      const eF = clamp((new Date(r.fine).getTime() - rangeStart.getTime()) / DAY, 0, days.length);
      if (eF <= 0 || sF >= days.length || eF <= sF) return;
      rentalBikes(r).forEach((rb) => ensure(rb.bike_id).push({ type: "rental", ref: r, sIdx: sF, eIdx: eF, rowBikeId: rb.bike_id, rowBikeNumero: rb.bike_numero }));
    });
    data.damages.forEach((d) => {
      const sIdx = clamp(Math.floor(colOffset(d.created_at || rangeStart)), 0, days.length);
      ensure(d.bike_id).push({ type: "damage", ref: d, sIdx, eIdx: days.length });
    });
    data.indisponibilita.forEach((d) => {
      const sIdx = clamp(Math.floor(colOffset(d.data_inizio)), 0, days.length);
      const eIdx = d.data_fine ? clamp(Math.ceil((new Date(d.data_fine).getTime() - rangeStart.getTime()) / DAY), 0, days.length) : days.length;
      ensure(d.bike_id).push({ type: "indispo", ref: d, sIdx, eIdx });
    });
    return map;
  }, [data, days, rangeStart]);

  // --- Vista giornaliera: occupazione per fascia oraria ---
  const anchorISO = isoDate(anchor);
  const fasceCols = useMemo(
    () => [...timeslots].sort((a, b) => (a.ordine ?? 99) - (b.ordine ?? 99) || (a.ora_inizio || "").localeCompare(b.ora_inizio || "")),
    [timeslots]
  );
  const rentalDaySlot = useCallback((r) => {
    if (Array.isArray(r.giorni) && r.giorni.length) {
      const g = r.giorni.find((x) => x.data === anchorISO);
      if (!g) return null;
      return { oi: g.ora_inizio || "00:00", of: g.ora_fine || "23:59", fascia_id: g.fascia_id, fascia_nome: g.fascia_nome };
    }
    const di = isoDate(new Date(r.inizio)), df = isoDate(new Date(r.fine));
    if (anchorISO < di || anchorISO > df) return null;
    const oi = anchorISO === di ? new Date(r.inizio).toTimeString().slice(0, 5) : "00:00";
    const of = anchorISO === df ? new Date(r.fine).toTimeString().slice(0, 5) : "23:59";
    return { oi, of };
  }, [anchorISO]);

  const dayGrid = useMemo(() => {
    if (view !== "day") return {};
    const grid = {};
    const cell = (bid, fid) => { (grid[bid] = grid[bid] || {}); (grid[bid][fid] = grid[bid][fid] || { blocks: [], busy: false }); return grid[bid][fid]; };
    const overlaps = (s, c) => s.oi < (c.ora_fine || "23:59") && (c.ora_inizio || "00:00") < s.of;
    data.rentals.forEach((r) => {
      const slot = rentalDaySlot(r);
      if (!slot) return;
      rentalBikes(r).forEach((rb) => {
        const spost = (r.spostamenti_richiesti || []).some((s) => s.bike_id === rb.bike_id && !s.completato) || (!isMultiRental(r) && r.richiede_spostamento);
        let primary = slot.fascia_id ? fasceCols.find((c) => c.id === slot.fascia_id) : null;
        if (!primary) primary = fasceCols.find((c) => overlaps(slot, c));
        fasceCols.forEach((c) => {
          if (!overlaps(slot, c)) return;
          const cc = cell(rb.bike_id, c.id);
          cc.busy = true;
          if (primary && c.id === primary.id) cc.blocks.push({ type: "rental", ref: r, rowBikeId: rb.bike_id, rowBikeNumero: rb.bike_numero, slot, spost });
        });
      });
    });
    data.damages.forEach((d) => {
      if (d.created_at && isoDate(new Date(d.created_at)) > anchorISO) return;
      fasceCols.forEach((c) => { const cc = cell(d.bike_id, c.id); cc.busy = true; cc.blocks.push({ type: "damage", ref: d }); });
    });
    data.indisponibilita.forEach((d) => {
      const di = isoDate(new Date(d.data_inizio)), df = d.data_fine ? isoDate(new Date(d.data_fine)) : null;
      if (anchorISO >= di && (df === null || anchorISO <= df)) fasceCols.forEach((c) => { const cc = cell(d.bike_id, c.id); cc.busy = true; cc.blocks.push({ type: "indispo", ref: d }); });
    });
    return grid;
  }, [view, data, fasceCols, rentalDaySlot, anchorISO]);

  const onDayCellClick = (bikeId, fasciaId) => {
    const cat = (data.bikes.find((b) => b.id === bikeId)?.categoria_noleggio) || "";
    navigate(`/noleggi?new=1&bike_id=${bikeId}&date=${anchorISO}&fascia_id=${fasciaId}&categoria_id=${cat}`);
  };

  // --- interactions ---
  const [detail, setDetail] = useState(null); // rental detail/move dialog
  const [moveForm, setMoveForm] = useState(null);
  const [savingMove, setSavingMove] = useState(false);
  const [indForm, setIndForm] = useState(null); // out-of-service dialog
  const [dragInfo, setDragInfo] = useState(null);
  const [priceConfirm, setPriceConfirm] = useState(null);

  const openDetail = (r) => {
    setDetail(r);
    if (isMultiRental(r)) setMoveForm(null);
    else setMoveForm({ bike_id: r.bike_id, inizio: toLocalInput(new Date(r.inizio)), fine: toLocalInput(new Date(r.fine)) });
  };
  const sameCatBikes = useMemo(() => {
    if (!detail) return [];
    const cat = allBikes.find((b) => b.id === detail.bike_id)?.categoria_noleggio;
    return allBikes.filter((b) => (b.categoria_noleggio || "") === (cat || ""));
  }, [detail, allBikes]);

  const saveMove = async () => {
    setSavingMove(true);
    try {
      await api.put(`/rentals/${detail.id}/reschedule`, {
        bike_id: moveForm.bike_id,
        inizio: new Date(moveForm.inizio).toISOString(),
        fine: new Date(moveForm.fine).toISOString(),
      });
      toast.success("Prenotazione aggiornata"); setDetail(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingMove(false); }
  };

  const onCellClick = (bikeId, date) => {
    const cat = (data.bikes.find((b) => b.id === bikeId)?.categoria_noleggio) || "";
    navigate(`/noleggi?new=1&bike_id=${bikeId}&date=${isoDate(date)}&categoria_id=${cat}`);
  };

  // HTML5 drag: move a rental block to another cell (bike row + day)
  const onBlockDragStart = (e, b) => {
    if (!canEdit || b.type !== "rental" || !["richiesta", "confermata", "in_attesa_caparra"].includes(b.ref.stato)) { e.preventDefault(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const grabCol = Math.floor(((e.clientX || 0) - rect.left) / dayWidth);
    const bikeIds = new Set(rentalBikes(b.ref).map((rb) => rb.bike_id));
    setDragInfo({ rental: b.ref, durationDays: b.eIdx - b.sIdx, grabCol, multi: isMultiRental(b.ref), bikeIds });
    e.dataTransfer.effectAllowed = "move";
  };
  const groupReschedule = async (r, giorni, inizio, fine, confirm) => {
    const body = { confirm_price_change: !!confirm };
    if (giorni) body.giorni = giorni; else { body.inizio = inizio; body.fine = fine; }
    const res = await api.put(`/rentals/${r.id}/reschedule-group`, body);
    return res.data;
  };
  const onCellDrop = async (e, bikeId, date) => {
    e.preventDefault();
    if (!dragInfo) return;
    const r = dragInfo.rental;
    const info = dragInfo;
    setDragInfo(null);
    const origInizio = new Date(r.inizio);
    const newStartDay = dayStart(new Date(date.getTime() - info.grabCol * DAY));
    if (info.multi) {
      // Sposta l'INTERA prenotazione: shift di tutti i giorni per lo stesso delta, stesse bici
      const deltaDays = Math.round((newStartDay.getTime() - dayStart(origInizio).getTime()) / DAY);
      if (deltaDays === 0) return;
      let giorni = null;
      if (Array.isArray(r.giorni) && r.giorni.length) {
        giorni = r.giorni.map((g) => {
          const nd = new Date(`${g.data}T00:00:00`); nd.setDate(nd.getDate() + deltaDays);
          return { data: isoDate(nd), fascia_id: g.fascia_id };
        });
      }
      let inizio = null, fine = null;
      if (!giorni) {
        const ni = new Date(newStartDay); ni.setHours(origInizio.getHours(), origInizio.getMinutes(), 0, 0);
        const durMs = new Date(r.fine).getTime() - new Date(r.inizio).getTime();
        inizio = ni.toISOString(); fine = new Date(ni.getTime() + durMs).toISOString();
      }
      try {
        const data = await groupReschedule(r, giorni, inizio, fine, false);
        if (data && data.needs_confirmation) {
          setPriceConfirm({ rental: r, giorni, inizio, fine, ...data });
          return;
        }
        toast.success("Prenotazione spostata"); await load();
      } catch (err) { toast.error(err.response?.data?.detail || "Spostamento non consentito"); }
      return;
    }
    // Single-bike: comportamento invariato (può cambiare anche bici)
    const newInizio = new Date(newStartDay); newInizio.setHours(origInizio.getHours(), origInizio.getMinutes(), 0, 0);
    const durMs = new Date(r.fine).getTime() - new Date(r.inizio).getTime();
    const newFine = new Date(newInizio.getTime() + durMs);
    try {
      await api.put(`/rentals/${r.id}/reschedule`, { bike_id: bikeId, inizio: newInizio.toISOString(), fine: newFine.toISOString() });
      toast.success("Prenotazione spostata"); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Spostamento non consentito"); }
  };
  const confirmGroupPrice = async () => {
    if (!priceConfirm) return;
    const { rental, giorni, inizio, fine } = priceConfirm;
    setPriceConfirm(null);
    try {
      await groupReschedule(rental, giorni, inizio, fine, true);
      toast.success("Prenotazione spostata"); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Spostamento non consentito"); }
  };

  const openIndispo = (bikeId, date) => {
    setIndForm({ bike_id: bikeId, data_inizio: isoDate(date || new Date()), data_fine: "", motivo: "manutenzione", note: "" });
  };
  const saveIndispo = async () => {
    try {
      await api.post("/indisponibilita", {
        bike_id: indForm.bike_id,
        data_inizio: new Date(`${indForm.data_inizio}T00:00:00`).toISOString(),
        data_fine: indForm.data_fine ? new Date(`${indForm.data_fine}T23:59:00`).toISOString() : null,
        motivo: indForm.motivo, note: indForm.note,
      });
      toast.success("Periodo di indisponibilità creato"); setIndForm(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deleteIndispo = async (id) => {
    try { await api.delete(`/indisponibilita/${id}`); toast.success("Rimosso"); setDetail(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const periodLabel = view === "day"
    ? anchor.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })
    : view === "week"
      ? `${fmtDay(days[0])} – ${fmtDay(days[days.length - 1])}`
      : anchor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="p-5 sm:p-8 fade-in">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Pianificazione flotta</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight flex items-center gap-3"><CalendarRange className="h-9 w-9 text-[#f98125]" /> CALENDARIO</h1>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-[#141414] border border-zinc-800 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button onClick={() => nav(-1)} data-testid="cal-prev" className="p-2 hover:bg-zinc-800 rounded-sm"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setAnchor(dayStart(new Date()))} data-testid="cal-today" className="px-3 py-1.5 text-sm hover:bg-zinc-800 rounded-sm">Oggi</button>
          <button onClick={() => nav(1)} data-testid="cal-next" className="p-2 hover:bg-zinc-800 rounded-sm"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="font-display text-xl tracking-tight min-w-[180px] capitalize" data-testid="cal-period">{periodLabel}</div>
        <div className="flex rounded-sm border border-zinc-800 overflow-hidden">
          {[["day", "Giorno"], ["week", "Settimana"], ["month", "Mese"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`px-3 py-1.5 text-sm ${view === v ? "bg-[#f98125] text-white" : "text-zinc-400 hover:bg-zinc-800"}`}>{lbl}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-zinc-500" />
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 w-44" data-testid="cal-categoria"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800">
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          {sedi.length > 0 && (
            <Select value={sede} onValueChange={setSede}>
              <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 w-44" data-testid="cal-sede"><SelectValue placeholder="Sede" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">
                <SelectItem value="all">Tutte le sedi</SelectItem>
                {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                <SelectItem value="none">Senza sede</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto bg-[#0A0A0A] border border-zinc-800 rounded-sm px-3 py-1.5">
          <Switch checked={availOn} onCheckedChange={setAvailOn} data-testid="cal-avail-toggle" />
          <span className="text-xs text-zinc-400">Solo disponibili</span>
          <Input type="date" value={availStart} onChange={(e) => setAvailStart(e.target.value)} data-testid="cal-avail-start" className="h-8 w-36 bg-[#141414] border-zinc-800 rounded-sm text-xs" />
          <span className="text-zinc-600">→</span>
          <Input type="date" value={availEnd} onChange={(e) => setAvailEnd(e.target.value)} data-testid="cal-avail-end" className="h-8 w-36 bg-[#141414] border-zinc-800 rounded-sm text-xs" />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3 text-[11px] text-zinc-400">
        {Object.entries(STATO_LABEL).map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded-sm ${STATO_COLORS[k].split(" ")[0]}`} /> {v}</span>)}
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#FF3B30]" /> Danno grave</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-violet-500" /> Fuori servizio</span>
      </div>

      {/* Timeline */}
      <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-hidden">
        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#f98125]" /></div>
        ) : visibleBikes.length === 0 ? (
          <div className="py-24 text-center text-zinc-600 text-sm" data-testid="cal-empty">Nessuna bici da mostrare. {data.bikes.length === 0 && "Aggiungi bici dall'Inventario."}</div>
        ) : view === "day" ? (
          <div className="overflow-x-auto" data-testid="cal-day-grid">
            {fasceCols.length === 0 ? (
              <div className="py-24 text-center text-zinc-600 text-sm">Nessuna fascia oraria configurata. Creale in Listini → Fasce orarie.</div>
            ) : (
              <div style={{ minWidth: 160 + fasceCols.length * 150 }}>
                {/* header */}
                <div className="grid sticky top-0 z-10 bg-[#0d0d0d] border-b border-zinc-800" style={{ gridTemplateColumns: `160px repeat(${fasceCols.length}, minmax(140px, 1fr))` }}>
                  <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-r border-zinc-800">Bici</div>
                  {fasceCols.map((c) => (
                    <div key={c.id} className="px-3 py-2 border-r border-zinc-800/60 text-center">
                      <div className="text-xs font-semibold text-zinc-200">{c.nome}</div>
                      <div className="text-[10px] text-zinc-500 flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> {c.ora_inizio}–{c.ora_fine}</div>
                    </div>
                  ))}
                </div>
                {/* rows */}
                {visibleBikes.map((bike) => (
                  <div key={bike.id} data-testid={`cal-day-row-${bike.id}`} className="grid border-b border-zinc-800/60 hover:bg-white/[0.015]" style={{ gridTemplateColumns: `160px repeat(${fasceCols.length}, minmax(140px, 1fr))` }}>
                    <div className="px-3 py-3 border-r border-zinc-800 flex items-center gap-2 group">
                      <span className="font-display text-lg text-[#f98125]">{bike.numero}</span>
                      <span className="text-xs text-zinc-500 truncate flex-1">{bike.modello}</span>
                      {hasPerm("maintenance.create") && (
                        <button onClick={() => openIndispo(bike.id, anchor)} data-testid={`cal-day-oos-${bike.id}`} title="Metti fuori servizio" className="p-1 text-zinc-600 hover:text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity"><Wrench className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                    {fasceCols.map((c) => {
                      const info = (dayGrid[bike.id] || {})[c.id] || { blocks: [], busy: false };
                      if (info.blocks.length === 0 && !info.busy) {
                        return (
                          <div key={c.id} onClick={() => onDayCellClick(bike.id, c.id)} data-testid={`cal-day-cell-${bike.id}-${c.id}`}
                            className="border-r border-zinc-800/40 p-1.5 min-h-[56px] cursor-pointer hover:bg-[#f98125]/[0.06] flex items-center justify-center group">
                            <Plus className="h-4 w-4 text-zinc-700 group-hover:text-[#f98125]" />
                          </div>
                        );
                      }
                      if (info.blocks.length === 0 && info.busy) {
                        return (
                          <div key={c.id} data-testid={`cal-day-busy-${bike.id}-${c.id}`}
                            className="border-r border-zinc-800/40 p-1.5 min-h-[56px] flex items-center justify-center bg-zinc-800/20">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-600">Occupata</span>
                          </div>
                        );
                      }
                      return (
                        <div key={c.id} data-testid={`cal-day-cell-${bike.id}-${c.id}`} className="border-r border-zinc-800/40 p-1.5 min-h-[56px] space-y-1">
                          {info.blocks.map((b, idx) => {
                            if (b.type === "rental") {
                              const multi = isMultiRental(b.ref);
                              return (
                                <div key={idx} onClick={() => openDetail(b.ref)} data-testid={`cal-day-rental-${b.ref.id}`}
                                  title={`${b.ref.customer_nome}${multi ? ` · ${b.ref.bici.length} bici` : ""}`}
                                  className={`rounded-sm px-2 py-1.5 cursor-pointer shadow-sm ${STATO_COLORS[b.ref.stato] || "bg-zinc-600"} ${b.spost ? "ring-2 ring-amber-400 ring-inset" : ""}`}>
                                  <div className="text-[11px] font-semibold leading-tight truncate flex items-center gap-1">{b.spost && <ArrowLeftRight className="h-3 w-3 shrink-0 text-amber-300" />}{multi && <span className="inline-flex items-center gap-0.5 shrink-0"><Bike className="h-3 w-3" />{b.ref.bici.length}</span>}{b.ref.customer_nome}</div>
                                  <div className="text-[9px] opacity-80 leading-tight truncate">{STATO_LABEL[b.ref.stato]}{b.ref.canale === "online" ? " · online" : ""}</div>
                                </div>
                              );
                            }
                            const isDamage = b.type === "damage";
                            return (
                              <div key={idx} onClick={() => setDetail({ _block: b.type, ...b.ref })} data-testid={`cal-day-${b.type}-${b.ref.id}`}
                                style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 6px, transparent 6px 12px)" }}
                                className={`rounded-sm px-2 py-1.5 cursor-pointer text-white ${isDamage ? "bg-[#FF3B30]" : "bg-violet-500"}`}>
                                <div className="text-[11px] font-semibold leading-tight truncate flex items-center gap-1">{isDamage ? <AlertTriangle className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}{isDamage ? "Danno grave" : (MOTIVO_LABEL[b.ref.motivo] || "Fuori servizio")}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: 180 + days.length * dayWidth }}>
              {/* header */}
              <div className="flex sticky top-0 z-10 bg-[#0d0d0d] border-b border-zinc-800">
                <div className="w-[180px] shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-r border-zinc-800">Bici</div>
                {days.map((d, i) => {
                  const today = isoDate(d) === isoDate(new Date());
                  return (
                    <div key={i} style={{ width: dayWidth }} className={`shrink-0 text-center py-2 border-r border-zinc-800/60 ${today ? "bg-[#f98125]/10" : ""}`}>
                      <div className="text-[10px] uppercase text-zinc-500">{fmtWeekday(d)}</div>
                      <div className={`text-sm ${today ? "text-[#f98125] font-bold" : "text-zinc-300"}`}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
              {/* rows */}
              {visibleBikes.map((bike) => (
                <div key={bike.id} data-testid={`cal-row-${bike.id}`} className={`flex border-b border-zinc-800/60 hover:bg-white/[0.015] ${dragInfo && dragInfo.multi && dragInfo.bikeIds.has(bike.id) ? "bg-[#39FF14]/[0.07]" : ""}`}>
                  <div className="w-[180px] shrink-0 px-3 py-3 border-r border-zinc-800 flex items-center gap-2 group">
                    <span className="font-display text-lg text-[#f98125]">{bike.numero}</span>
                    <span className="text-xs text-zinc-500 truncate flex-1">{bike.modello}</span>
                    {hasPerm("maintenance.create") && (
                      <button onClick={() => openIndispo(bike.id, days[0])} data-testid={`cal-oos-${bike.id}`} title="Metti fuori servizio" className="p-1 text-zinc-600 hover:text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity"><Wrench className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                  <div className="relative flex-1" style={{ height: 52 }}>
                    {/* day cells (drop + click) */}
                    <div className="absolute inset-0 flex">
                      {days.map((d, i) => (
                        <div key={i} style={{ width: dayWidth }}
                          onClick={() => onCellClick(bike.id, d)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => onCellDrop(e, bike.id, d)}
                          data-testid={`cal-cell-${bike.id}-${isoDate(d)}`}
                          className="shrink-0 border-r border-zinc-800/40 cursor-pointer hover:bg-[#f98125]/[0.06]" />
                      ))}
                    </div>
                    {/* blocks */}
                    {(blocksByBike[bike.id] || []).map((b, idx) => {
                      const left = b.sIdx * dayWidth + 2;
                      const rawW = (b.eIdx - b.sIdx) * dayWidth - 4;
                      const width = b.type === "rental" ? Math.max(22, rawW) : Math.max(dayWidth - 4, rawW);
                      if (b.type === "rental") {
                        const slotLbl = b.ref.giorni?.length === 1
                          ? (b.ref.giorni[0].fascia_nome || "")
                          : b.ref.giorni?.length > 1
                            ? `${b.ref.giorni.length} giorni`
                            : `${new Date(b.ref.inizio).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}–${new Date(b.ref.fine).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
                        const multi = isMultiRental(b.ref);
                        const rowSpost = (b.ref.spostamenti_richiesti || []).some((s) => s.bike_id === b.rowBikeId && !s.completato) || (!multi && b.ref.richiede_spostamento);
                        const draggable = canEdit && ["richiesta", "confermata", "in_attesa_caparra"].includes(b.ref.stato);
                        const dragging = dragInfo && dragInfo.rental.id === b.ref.id;
                        return (
                          <div key={idx} draggable={draggable}
                            onDragStart={(e) => onBlockDragStart(e, b)}
                            onClick={(e) => { e.stopPropagation(); openDetail(b.ref); }}
                            data-testid={`cal-rental-${b.ref.id}`}
                            title={`${b.ref.customer_nome} · ${slotLbl}${multi ? ` · ${b.ref.bici.length} bici` : ""}`}
                            style={{ left, width, top: 6, height: 40 }}
                            className={`absolute rounded-sm px-1.5 py-1 overflow-hidden cursor-pointer shadow-sm ${STATO_COLORS[b.ref.stato] || "bg-zinc-600"} ${rowSpost ? "ring-2 ring-amber-400 ring-inset" : ""} ${draggable ? "active:cursor-grabbing" : ""} ${dragging ? "ring-2 ring-[#39FF14] ring-inset opacity-80" : ""}`}>
                            <div className="text-[11px] font-semibold leading-tight truncate flex items-center gap-1">{rowSpost && <ArrowLeftRight className="h-3 w-3 shrink-0 text-amber-300" />}{multi && <span className="inline-flex items-center gap-0.5 shrink-0"><Bike className="h-3 w-3" />{b.ref.bici.length}</span>}{b.ref.customer_nome}</div>
                            <div className="text-[9px] opacity-80 leading-tight truncate">{slotLbl}{b.ref.canale === "online" ? " · online" : ""}</div>
                          </div>
                        );
                      }
                      const isDamage = b.type === "damage";
                      return (
                        <div key={idx} onClick={(e) => { e.stopPropagation(); setDetail({ _block: b.type, ...b.ref }); }}
                          data-testid={`cal-${b.type}-${b.ref.id}`}
                          style={{ left, width, top: 6, height: 40, backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 6px, transparent 6px 12px)" }}
                          className={`absolute rounded-sm px-2 py-1 overflow-hidden cursor-pointer text-white ${isDamage ? "bg-[#FF3B30]" : "bg-violet-500"}`}>
                          <div className="text-[11px] font-semibold leading-tight truncate flex items-center gap-1">{isDamage ? <AlertTriangle className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}{isDamage ? "Danno grave" : (MOTIVO_LABEL[b.ref.motivo] || "Fuori servizio")}</div>
                          <div className="text-[9px] opacity-90 leading-tight truncate">{isDamage ? b.ref.descrizione : (b.ref.note || (b.ref.data_fine ? "" : "a tempo indet."))}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-zinc-600 mt-3">Vista <b className="text-zinc-400">Giorno</b>: una riga per bici, colonne per fascia oraria; clic su una cella libera per creare una prenotazione su quella bici/fascia. Viste <b className="text-zinc-400">Settimana/Mese</b>: clic su una cella libera per creare · clic su un blocco per aprirlo · trascina una prenotazione (richiesta/confermata) per spostarla.</p>

      {/* Rental detail / move dialog */}
      <Dialog open={!!detail && !detail._block} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md">
          {detail && !detail._block && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{isMultiRental(detail) ? `Prenotazione · ${detail.bici.length} bici` : `Prenotazione · Bici ${detail.bike_numero || ""}`}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Cliente</span><span>{detail.customer_nome}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Stato</span><span><span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm ${STATO_COLORS[detail.stato] || "bg-zinc-600"}`}>{STATO_LABEL[detail.stato]}</span></span></div>
                {isMultiRental(detail) && (
                  <div className="flex items-start gap-1.5 text-zinc-300" data-testid="cal-detail-bikes"><Bike className="h-3.5 w-3.5 mt-0.5 text-[#f98125]" /><span>Bici: {detail.bici.map((b) => b.bike_numero).join(", ")}</span></div>
                )}
                {detail.sede_ritiro_nome && <div className="flex justify-between"><span className="text-zinc-500">Sede ritiro</span><span>{detail.sede_ritiro_nome}</span></div>}
                {detail.richiede_spostamento && (detail.spostamenti_richiesti || []).filter((s) => !s.completato).map((s) => (
                  <div key={s.bike_id} className="flex items-center gap-1.5 text-amber-400 bg-amber-500/10 rounded-sm px-2 py-1.5"><ArrowLeftRight className="h-3.5 w-3.5" /> Bici {s.bike_numero}: da {s.sede_partenza_nome} → a {s.sede_destinazione_nome}</div>
                ))}
                {isMultiRental(detail) && ["richiesta", "confermata", "in_attesa_caparra"].includes(detail.stato) && canEdit && (
                  <div className="text-[11px] text-zinc-400 bg-zinc-800/50 rounded-sm px-2 py-1.5" data-testid="cal-multi-hint"><b className="text-zinc-200">Trascina</b> il blocco sul calendario per spostare l'intero noleggio (tutte le bici insieme), oppure usa <b className="text-zinc-200">Modifica prenotazione</b> in Noleggi.</div>
                )}
                {moveForm && !isMultiRental(detail) && ["richiesta", "confermata"].includes(detail.stato) && canEdit ? (
                  <div className="space-y-3 pt-2 border-t border-zinc-800">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Sposta / estendi</div>
                    <div><Label className="text-xs text-zinc-500">Bici (stessa categoria)</Label>
                      <Select value={moveForm.bike_id} onValueChange={(v) => setMoveForm({ ...moveForm, bike_id: v })}>
                        <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="move-bike"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-zinc-800">{sameCatBikes.map((b) => <SelectItem key={b.id} value={b.id}>{b.numero} · {b.modello}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs text-zinc-500">Inizio</Label><Input type="datetime-local" data-testid="move-inizio" value={moveForm.inizio} onChange={(e) => setMoveForm({ ...moveForm, inizio: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
                      <div><Label className="text-xs text-zinc-500">Fine</Label><Input type="datetime-local" data-testid="move-fine" value={moveForm.fine} onChange={(e) => setMoveForm({ ...moveForm, fine: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
                    </div>
                  </div>
                ) : (
                  <div className="text-zinc-500 text-xs">Dal {new Date(detail.inizio).toLocaleString("it-IT")} al {new Date(detail.fine).toLocaleString("it-IT")}</div>
                )}
              </div>
              <DialogFooter>
                <button onClick={() => navigate("/noleggi")} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Apri in Noleggi</button>
                {moveForm && ["richiesta", "confermata"].includes(detail.stato) && canEdit && (
                  <button onClick={saveMove} disabled={savingMove} data-testid="move-save" className="rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60">{savingMove ? "Salvataggio…" : "Salva"}</button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Block (damage/indispo) detail */}
      <Dialog open={!!detail && !!detail._block} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md">
          {detail && detail._block && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{detail._block === "damage" ? "Danno grave" : (MOTIVO_LABEL[detail.motivo] || "Fuori servizio")}</DialogTitle></DialogHeader>
              <div className="space-y-2 py-2 text-sm">
                {detail._block === "damage" ? (
                  <>
                    <div className="text-zinc-300">{detail.descrizione}</div>
                    <button onClick={() => navigate("/segnalazioni")} className="text-[#f98125] text-xs hover:underline">Gestisci in Segnalazioni →</button>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-zinc-500">Dal</span><span>{new Date(detail.data_inizio).toLocaleDateString("it-IT")}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Al</span><span>{detail.data_fine ? new Date(detail.data_fine).toLocaleDateString("it-IT") : "a tempo indeterminato"}</span></div>
                    {detail.note && <div className="text-zinc-400 text-xs">{detail.note}</div>}
                    {hasPerm("maintenance.create") && <button onClick={() => deleteIndispo(detail.id)} data-testid="delete-indispo" className="text-[#FF3B30] text-xs hover:underline mt-2">Rimuovi periodo di indisponibilità</button>}
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Out-of-service dialog */}
      <Dialog open={!!indForm} onOpenChange={(o) => !o && setIndForm(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md">
          {indForm && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Wrench className="h-5 w-5 text-violet-400" /> Metti fuori servizio</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-zinc-500">Dal</Label><Input type="date" data-testid="ind-dal" value={indForm.data_inizio} onChange={(e) => setIndForm({ ...indForm, data_inizio: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
                  <div><Label className="text-xs text-zinc-500">Al (vuoto = indet.)</Label><Input type="date" data-testid="ind-al" value={indForm.data_fine} onChange={(e) => setIndForm({ ...indForm, data_fine: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
                </div>
                <div><Label className="text-xs text-zinc-500">Motivo</Label>
                  <Select value={indForm.motivo} onValueChange={(v) => setIndForm({ ...indForm, motivo: v })}>
                    <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="ind-motivo"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800">
                      <SelectItem value="manutenzione">Manutenzione</SelectItem>
                      <SelectItem value="fuori_servizio">Fuori servizio</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-zinc-500">Note</Label><Input data-testid="ind-note" value={indForm.note} onChange={(e) => setIndForm({ ...indForm, note: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              </div>
              <DialogFooter>
                <button onClick={() => setIndForm(null)} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
                <button onClick={saveIndispo} data-testid="ind-save" className="rounded-sm bg-violet-500 text-white font-bold px-4 py-2 text-sm hover:bg-violet-600">Salva</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Conferma cambio prezzo su spostamento multi-bici */}
      <Dialog open={!!priceConfirm} onOpenChange={(o) => !o && setPriceConfirm(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md" data-testid="price-confirm-dialog">
          {priceConfirm && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><ArrowLeftRight className="h-5 w-5 text-[#f98125]" /> Conferma spostamento</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <p className="text-zinc-400">Il nuovo periodo cambia l'importo del noleggio{["confermata", "in_attesa_caparra"].includes(priceConfirm.stato) ? " (prenotazione già confermata/in attesa caparra)" : ""}. Vuoi procedere?</p>
                <div className="flex justify-between"><span className="text-zinc-500">Totale</span><span><span className="line-through text-zinc-600 mr-2">{eur(priceConfirm.old_total)}</span><b className="text-[#f98125]" data-testid="price-confirm-new-total">{eur(priceConfirm.new_total)}</b></span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Caparra</span><span><span className="line-through text-zinc-600 mr-2">{eur(priceConfirm.old_caparra)}</span><b>{eur(priceConfirm.new_caparra)}</b></span></div>
              </div>
              <DialogFooter>
                <button onClick={() => setPriceConfirm(null)} data-testid="price-confirm-cancel" className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
                <button onClick={confirmGroupPrice} data-testid="price-confirm-ok" className="rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12]">Conferma spostamento</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
